import {jsx} from 'panel';
import type {VNode} from 'snabbdom';

import {MPProperties} from 'common/constants';
import {TooltipEl} from 'common/components/tooltip';
import type {TableRow} from 'common/components/table/types';
import {getProfileUrl} from 'common/util/format-property';
import {TrackedUserIconEl} from 'common/widgets/tracked-user-icon';
import {renameEvent} from 'common/report/util/events';
import {formatEventProperty} from 'common/report/events/utils';
import {ProfileIconClickEvent, ViewInInsightsClickEvent, ViewInLexiconClickEvent} from 'common/report/events/types';
import {SelectEl} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ChangeEvent} from 'common/components/select/types';

import {TrackEvents} from './tracking';

export function renderCellProperty(row: TableRow, property: string): VNode {
  const {formattedValue: propertyValue} = formatEventProperty(property, row.properties[property], {
    relativeTime: true,
  });
  let tooltip = propertyValue;
  if (property === MPProperties.Time) {
    const {formattedValue} = formatEventProperty(property, row.properties[property], {
      relativeTime: false,
    });
    tooltip = formattedValue;
  }
  return (
    <div sel='.cell-col'>
      {propertyValue}
      <TooltipEl>{tooltip}</TooltipEl>
    </div>
  );
}

export function renderCell(row: TableRow, name?: string, dataGroupId?: string): VNode {
  switch (name) {
    case `$event_name`:
      return renderEventName(row);
    case `$distinct_id`:
      return renderDistinctId(row, dataGroupId);
    case `$time`:
      return renderCellProperty(row, `time`);
    default:
      return renderCellProperty(row, name);
  }
}

export function trackClick(ev: MouseEvent, profileUrl: string, propName: string) {
  // prevent row from being expanded when distinct id is clicked
  ev.stopPropagation();
  if (propName === `Distinct ID`) {
    mixpanel.track(TrackEvents.ProfileClicked, {
      clicked: `distinct-id`,
      url: profileUrl,
    });
  }
}

function renderDistinctId(row: TableRow, dataGroupId?: string): VNode {
  const distinctId = row.properties.distinct_id;
  // TODO see if we can get this dataGroupId stuff into format-property
  const profileUrl = getProfileUrl(distinctId, dataGroupId);

  return (
    <div sel='.cell-distinct-id'>
      {distinctId ? (
        <>
          <a
            attrs={{href: profileUrl, target: `_blank`, rel: `noopener noreferrer`}}
            on={{click: (ev) => trackClick(ev, profileUrl, `Distinct ID`)}}
          >
            {distinctId}
          </a>
          {distinctId ? <TooltipEl>{distinctId}</TooltipEl> : null}
        </>
      ) : (
        `-`
      )}
    </div>
  );
}

function renderEventName(row: TableRow): VNode {
  const eventName = renameEvent(row.event);
  return (
    <div sel='.cell-event'>
      <div sel='.cell-event-name'>{eventName}</div>
      <TooltipEl>{eventName}</TooltipEl>
    </div>
  );
}

export function renderProfileIcon(
  row: TableRow,
  rowIdx: number,
  selectedDistinctId: string,
  selectedRowIdx: number,
): VNode {
  const distinctId = row.properties.distinct_id;
  const email = row.properties.Email;

  const handleProfileIconClick = (ev: Event, rowIdx: number, distinctId: string) => {
    // prevent row from being expanded when profile icon is clicked
    ev.stopPropagation();
    if (distinctId) {
      const profileIconClickEvent: ProfileIconClickEvent = new CustomEvent(`profileIconClick`, {
        composed: true,
        bubbles: true,
        detail: {distinctId, rowIdx},
      });
      ev.target.dispatchEvent(profileIconClickEvent);
    }
  };

  return (
    <div
      sel='.cell-profile-icon'
      attrs={{
        title: distinctId ? `View Profile` : `No Distinct Id On Event`,
      }}
      class={{
        'cell-profile-icon-clickable': !!distinctId,
        'cell-profile-icon-selected': !!distinctId && selectedDistinctId === distinctId,
        'cell-profile-icon-active': !!distinctId && selectedRowIdx === rowIdx,
      }}
      on={{
        click: (ev) => handleProfileIconClick(ev, rowIdx, distinctId),
      }}
    >
      <TrackedUserIconEl
        attrs={{
          'distinct-id': distinctId,
          email,
          size: `small`,
        }}
      />
    </div>
  );
}

const handleOverflowChange = (ev: ChangeEvent, row: TableRow) => {
  const {value} = ev.detail.item;
  switch (value) {
    case `viewInInsights`: {
      const viewInInsightsClickEvent: ViewInInsightsClickEvent = new CustomEvent(`viewInInsightsClick`, {
        composed: true,
        bubbles: true,
        detail: {eventName: row.event},
      });
      ev.target.dispatchEvent(viewInInsightsClickEvent);
      break;
    }
    case `viewInLexicon`: {
      const viewInLexiconClickEvent: ViewInLexiconClickEvent = new CustomEvent(`viewInLexiconClick`, {
        composed: true,
        bubbles: true,
        detail: {eventName: row.event},
      });
      ev.target.dispatchEvent(viewInLexiconClickEvent);
      break;
    }
    default:
      throw Error(`Unexpected value for overflow menu selection: ${value}`);
  }
};

export function renderActionsMenu(row: TableRow): VNode {
  const viewInInsights = {
    label: `View in Insights`,
    value: `viewInInsights`,
  };
  const viewInLexicon = {
    label: `View in Lexicon`,
    value: `viewInLexicon`,
  };

  return (
    <SelectEl
      attrs={{
        icon: {leftIcon: `hor-ellipsis`},
        'icon-only': true,
        open: false,
        theme: SelectTheme.Tertiary,
        'disable-search': true,
        'menu-width': 160,
        elref: `overflowMenu`,
      }}
      on={{
        click: (ev) => {
          // prevent row from being expanded when clicked
          ev.stopPropagation();
        },
        change: (ev) => handleOverflowChange(ev, row),
      }}
      props={{
        sectionsFn: () => [
          {
            items: [viewInInsights, viewInLexicon],
          },
        ],
      }}
    />
  );
}
